import { DateTime } from "luxon";

import type { CalendarEvent, CalendarEventFilterQuery } from "../Models/Domain";

import { getCalendarsEvents } from "@/Services/TherapyApi/Calendars";
import CalendarEventTransformer from "@/Modules/Transformers/Calendars/Event";

import useCalendarsStore from "../Store";
import useUserStore from "@/Modules/User/Store";
import { CALENDAR_CLIENT_UUID } from "./fetchCalendars";

import { hasValue } from "@/Utils/Objects";

const updateCalendarQueryActionHandler = async (
  newQuery: CalendarEventFilterQuery
): Promise<CalendarEvent[]> => {
  const calendarsStore = useCalendarsStore();
  const userStore = useUserStore();
  const calls: Promise<{ fromDate?: number; toDate?: number; events: CalendarEvent[] }>[] = [];

  if (hasValue(newQuery.fromDate) && calendarsStore.query.fromDate! > newQuery.fromDate) {
    const fromDate = DateTime.fromSeconds(newQuery.fromDate).minus({ months: 2 }).toUnixInteger();

    calls.push(
      getCalendarsEvents({
        from_date: fromDate,
        to_date: calendarsStore.query.fromDate,
        patient_uuid: calendarsStore.query.patientUuid,
        is_deleted: calendarsStore.query.isDeleted,
      }).then((list) => ({
        fromDate,
        events: list.map((event) =>
          CalendarEventTransformer.toDomain({
            ...event,
            calendar_uuid: userStore.getUserIsTherapist ? event.calendar_uuid : CALENDAR_CLIENT_UUID,
          })
        ),
      }))
    );
  }

  if (hasValue(newQuery.toDate) && calendarsStore.query.toDate! < newQuery.toDate) {
    const toDate = DateTime.fromSeconds(newQuery.toDate).plus({ months: 2 }).toUnixInteger();

    calls.push(
      getCalendarsEvents({
        from_date: calendarsStore.query.toDate,
        to_date: toDate,
        patient_uuid: calendarsStore.query.patientUuid,
        is_deleted: calendarsStore.query.isDeleted,
      }).then((list) => ({
        toDate,
        events: list.map((event) =>
          CalendarEventTransformer.toDomain({
            ...event,
            calendar_uuid: userStore.getUserIsTherapist ? event.calendar_uuid : CALENDAR_CLIENT_UUID,
          })
        ),
      }))
    );
  }

  if (calls.length > 0) {
    calendarsStore.isLoadingMore = true;

    try {
      const results = await Promise.all(calls);
      const eventsByUuid = new Map<string, CalendarEvent>(
        calendarsStore.events.map((event) => [event.uuid, event] as const)
      );

      results.forEach((result) => {
        result.events.forEach((event) => eventsByUuid.set(event.uuid, event));

        if (hasValue(result.fromDate)) calendarsStore.query.fromDate = result.fromDate;
        if (hasValue(result.toDate)) calendarsStore.query.toDate = result.toDate;
      });

      calendarsStore.events = Array.from(eventsByUuid.values());
    } finally {
      calendarsStore.isLoadingMore = false;
    }
  }

  return calendarsStore.events;
};

export default updateCalendarQueryActionHandler;
